import React, { useMemo, useState } from 'react';
import { Tag, AlertTriangle, CheckCircle, Search, Layers, AlertOctagon, ChevronDown, ChevronRight, BookOpen, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, ModuleId } from '../../../types';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { MODULES } from '../../../data/modules';
import { tierToNiveauCode } from '../../../config/tiers';
import { getFamilyDefinition, FamilyDefinition, TAXONOMY } from '../../../config/taxonomyRegistry';
interface FamilyExplorerProps {
  questions: Question[];
}
type FamilyGroup = {
  id: string;
  module: ModuleId;
  questions: Question[];
  levels: Set<number>;
  skills: Set<string>;
  subSkills: Set<string>;
  isCrossLevel: boolean;
  definition?: FamilyDefinition;
};
type ModuleStats = {
  moduleId: ModuleId;
  totalQuestions: number;
  totalFamilies: number;
  questionsWithFamily: number;
  avgQuestionsPerFamily: number;
  crossLevelCount: number;
};
interface StatCardProps {
  label: string;
  value: string | number;
  icon: ReactNode;
  subtext?: string;
  isError?: boolean;
  isWarning?: boolean;
}
interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: ReactNode;
}
type FilterType = 'all' | 'cross-level' | 'single' | 'missing' | 'taxonomy-gap';
export function FamilyExplorer({
  questions
}: FamilyExplorerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState<ModuleId | 'all'>('all');
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<FilterType>('all');
  // Process Data
  const {
    moduleStats,
    familyGroups,
    globalStats,
    missingFamilyQuestions,
    taxonomyGaps
  } = useMemo(() => {
    const unlinked: Question[] = [];
    const modStats = new Map<ModuleId, {
      totalQuestions: number;
      questionsWithFamily: number;
      families: Set<string>;
      crossLevelFamilies: number;
    }>();
    MODULES.forEach(m => {
      modStats.set(m.id, {
        totalQuestions: 0,
        questionsWithFamily: 0,
        families: new Set<string>(),
        crossLevelFamilies: 0
      });
    });
    const familyMap = new Map<string, FamilyGroup>();
    questions.forEach(q => {
      const moduleStats = modStats.get(q.module);
      if (moduleStats) {
        moduleStats.totalQuestions++;
        if (q.familyId) {
          moduleStats.questionsWithFamily++;
          moduleStats.families.add(q.familyId);
        }
      }
      if (!q.familyId) {
        unlinked.push(q);
        return;
      }
      const definition = getFamilyDefinition(q.familyId);
      if (!familyMap.has(q.familyId)) {
        familyMap.set(q.familyId, {
          id: q.familyId,
          module: q.module,
          questions: [],
          levels: new Set(),
          skills: new Set(),
          subSkills: new Set(),
          isCrossLevel: false,
          definition: definition
        });
      }
      const familyNode = familyMap.get(q.familyId)!;
      familyNode.questions.push(q);
      familyNode.levels.add(q.difficulty);
      if (q.skill) familyNode.skills.add(q.skill);
      if (q.subSkill) familyNode.subSkills.add(q.subSkill);
    });
    const processedGroups = Array.from(familyMap.values()).map(g => {
      g.isCrossLevel = g.levels.size > 1;
      if (g.isCrossLevel) {
        const moduleStats = modStats.get(g.module);
        if (moduleStats) moduleStats.crossLevelFamilies++;
      }
      return g;
    });
    const finalModuleStats: ModuleStats[] = MODULES.map(m => {
      const s = modStats.get(m.id)!;
      const totalFamilies = s.families.size;
      const avg = totalFamilies > 0 ? s.questionsWithFamily / totalFamilies : 0;
      return {
        moduleId: m.id,
        totalQuestions: s.totalQuestions,
        totalFamilies: totalFamilies,
        questionsWithFamily: s.questionsWithFamily,
        avgQuestionsPerFamily: avg,
        crossLevelCount: s.crossLevelFamilies
      };
    });
    // Taxonomy gaps: families in TAXONOMY but with no seeded questions
    const seededFamilyIds = new Set(familyMap.keys());
    const gaps = TAXONOMY.filter(f => !seededFamilyIds.has(f.familyId)).sort((a, b) => a.tier - b.tier);
    return {
      moduleStats: finalModuleStats,
      familyGroups: processedGroups,
      globalStats: {
        totalFamilies: familyMap.size,
        crossLevelFamilies: processedGroups.filter(g => g.isCrossLevel).length,
        singleQuestionFamilies: processedGroups.filter(g => g.questions.length === 1).length,
        missingCount: unlinked.length,
        taxonomyTotal: TAXONOMY.length,
        taxonomyGapCount: gaps.length
      },
      missingFamilyQuestions: unlinked,
      taxonomyGaps: gaps
    };
  }, [questions]);
  const filteredFamilies = useMemo(() => {
    return familyGroups.filter(g => {
      const matchesSearch = g.id.toLowerCase().includes(searchTerm.toLowerCase()) || Array.from(g.subSkills).some(s => s.toLowerCase().includes(searchTerm.toLowerCase())) || g.definition?.description && g.definition.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesModule = selectedModule === 'all' || g.module === selectedModule;
      let matchesType = true;
      if (filterType === 'cross-level') matchesType = g.isCrossLevel;
      if (filterType === 'single') matchesType = g.questions.length === 1;
      return matchesSearch && matchesModule && matchesType;
    }).sort((a, b) => {
      if (a.isCrossLevel !== b.isCrossLevel) return a.isCrossLevel ? -1 : 1;
      return b.questions.length - a.questions.length;
    });
  }, [familyGroups, searchTerm, selectedModule, filterType]);
  const filteredGaps = useMemo(() => {
    return taxonomyGaps.filter(f => {
      const matchesSearch = searchTerm === '' || f.familyId.toLowerCase().includes(searchTerm.toLowerCase()) || f.description.toLowerCase().includes(searchTerm.toLowerCase()) || f.skill.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesModule = selectedModule === 'all' || f.module === selectedModule;
      return matchesSearch && matchesModule;
    });
  }, [taxonomyGaps, searchTerm, selectedModule]);
  const coveragePct = Math.round(globalStats.totalFamilies / globalStats.taxonomyTotal * 100);
  const toggleFamily = (id: string) => {
    const newSet = new Set(expandedFamilies);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setExpandedFamilies(newSet);
  };
  return <div className="space-y-6" data-id="element-1505">
      {/* Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-1506">
        <StatCard label="Taxonomy Total" value={globalStats.taxonomyTotal} icon={<BookOpen className="w-4 h-4 text-slate-500" data-id="element-1508" />} subtext="Defined in registry" data-id="element-1507" />
        <StatCard label="Seeded Families" value={globalStats.totalFamilies} icon={<Tag className="w-4 h-4 text-indigo-500" data-id="element-1510" />} subtext={`${coveragePct}% coverage`} data-id="element-1509" />
        <StatCard label="Taxonomy Gaps" value={globalStats.taxonomyGapCount} icon={<XCircle className="w-4 h-4 text-rose-500" data-id="element-1512" />} isError={globalStats.taxonomyGapCount > 0} subtext="Need seeds" data-id="element-1511" />
        <StatCard label="Single Question" value={globalStats.singleQuestionFamilies} icon={<AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-1514" />} isWarning={globalStats.singleQuestionFamilies > 0} data-id="element-1513" />
        <StatCard label="Missing FamilyId" value={globalStats.missingCount} icon={<AlertOctagon className="w-4 h-4 text-rose-500" data-id="element-1516" />} isError={globalStats.missingCount > 0} data-id="element-1515" />
      </div>

      {/* Coverage Progress Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-id="element-1517">
        <div className="flex items-center justify-between mb-2" data-id="element-1518">
          <span className="text-sm font-bold text-slate-700" data-id="element-1519">
            Taxonomy Coverage
          </span>
          <span className={`text-sm font-bold ${coveragePct >= 80 ? 'text-emerald-600' : coveragePct >= 50 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-1520">
            {globalStats.totalFamilies} / {globalStats.taxonomyTotal} families (
            {coveragePct}%)
          </span>
        </div>
        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-1521">
          <div className={`h-full rounded-full transition-all ${coveragePct >= 80 ? 'bg-emerald-500' : coveragePct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
          width: `${coveragePct}%`
        }} data-id="element-1522" />
        </div>
        <div className="flex justify-between mt-1" data-id="element-1523">
          <span className="text-xs text-slate-400" data-id="element-1524">0%</span>
          <span className="text-xs text-rose-500 font-medium" data-id="element-1525">
            {globalStats.taxonomyGapCount} families need seeds
          </span>
          <span className="text-xs text-slate-400" data-id="element-1526">100%</span>
        </div>
      </div>

      {/* Module Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1527">
        {moduleStats.map(stat => {
        const module = MODULES.find(m => m.id === stat.moduleId);
        if (!module) return null;
        const taxonomyFamiliesForModule = TAXONOMY.filter(f => f.module === stat.moduleId).length;
        const moduleCoverage = taxonomyFamiliesForModule > 0 ? Math.round(stat.totalFamilies / taxonomyFamiliesForModule * 100) : 100;
        return <Card key={stat.moduleId} className="p-4 flex flex-col h-full" data-id="element-1528">
              <div className="flex items-center justify-between mb-3" data-id="element-1529">
                <div className="flex items-center gap-2" data-id="element-1530">
                  <div className={`w-2 h-2 rounded-full ${module.color.replace('text-', 'bg-')}`} data-id="element-1531" />
                  <span className="font-bold text-sm text-slate-700" data-id="element-1532">
                    {module.title}
                  </span>
                </div>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${moduleCoverage === 100 ? 'bg-emerald-100 text-emerald-700' : moduleCoverage >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-1533">
                  {moduleCoverage}%
                </span>
              </div>
              <div className="space-y-2 mt-auto" data-id="element-1534">
                <div className="flex justify-between text-xs" data-id="element-1535">
                  <span className="text-slate-500" data-id="element-1536">Seeded / Total</span>
                  <span className="font-mono font-bold text-slate-700" data-id="element-1537">
                    {stat.totalFamilies} / {taxonomyFamiliesForModule}
                  </span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-1538">
                  <span className="text-slate-500" data-id="element-1539">Avg per Family</span>
                  <span className="font-mono font-bold text-slate-700" data-id="element-1540">
                    {stat.avgQuestionsPerFamily.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden mt-2" data-id="element-1541">
                  <div className={`h-full rounded-full ${moduleCoverage === 100 ? 'bg-emerald-500' : moduleCoverage >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
                width: `${moduleCoverage}%`
              }} data-id="element-1542" />
                </div>
              </div>
            </Card>;
      })}
      </div>

      {/* Main Explorer */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col h-[600px]" data-id="element-1543">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 bg-slate-50" data-id="element-1544">
          <div className="relative flex-1 min-w-[200px]" data-id="element-1545">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1546" />
            <input type="text" placeholder="Zoek op ID, concept of beschrijving..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1547" />
          </div>
          <select value={selectedModule} onChange={e => setSelectedModule(e.target.value as ModuleId | 'all')} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1548">
            <option value="all" data-id="element-1549">Alle Vakken</option>
            {MODULES.map(m => <option key={m.id} value={m.id} data-id="element-1550">
                {m.title}
              </option>)}
          </select>
          <div className="flex bg-white rounded-lg border border-slate-200 p-1 flex-wrap gap-1" data-id="element-1551">
            <FilterButton active={filterType === 'all'} onClick={() => setFilterType('all')} label="Alles" data-id="element-1552" />
            <FilterButton active={filterType === 'taxonomy-gap'} onClick={() => setFilterType('taxonomy-gap')} label={`Gaps (${globalStats.taxonomyGapCount})`} icon={<XCircle className="w-3 h-3" data-id="element-1554" />} data-id="element-1553" />
            <FilterButton active={filterType === 'cross-level'} onClick={() => setFilterType('cross-level')} label="Cross-Level" icon={<Layers className="w-3 h-3" data-id="element-1556" />} data-id="element-1555" />
            <FilterButton active={filterType === 'single'} onClick={() => setFilterType('single')} label="Single" icon={<AlertTriangle className="w-3 h-3" data-id="element-1558" />} data-id="element-1557" />
            <FilterButton active={filterType === 'missing'} onClick={() => setFilterType('missing')} label="No FamilyId" icon={<AlertOctagon className="w-3 h-3" data-id="element-1560" />} data-id="element-1559" />
          </div>
        </div>

        {/* List Content */}
        <div className="flex-1 overflow-y-auto p-0" data-id="element-1561">
          {filterType === 'missing' ? <div className="p-4 space-y-2" data-id="element-1562">
              <div className="text-sm font-bold text-rose-700 mb-2 flex items-center gap-2" data-id="element-1563">
                <AlertOctagon className="w-4 h-4" data-id="element-1564" />
                {missingFamilyQuestions.length} Vragen zonder FamilyId
              </div>
              {missingFamilyQuestions.map(q => <div key={q.id} className="flex items-center justify-between p-3 bg-rose-50 border border-rose-100 rounded-lg" data-id="element-1565">
                  <div data-id="element-1566">
                    <div className="font-mono text-xs font-bold text-rose-800" data-id="element-1567">
                      {q.id}
                    </div>
                    <div className="text-sm text-rose-900 line-clamp-1" data-id="element-1568">
                      {q.question}
                    </div>
                  </div>
                  <Badge variant="danger" size="sm" data-id="element-1569">
                    {q.module}
                  </Badge>
                </div>)}
              {missingFamilyQuestions.length === 0 && <div className="text-center py-12 text-emerald-600 flex flex-col items-center" data-id="element-1570">
                  <CheckCircle className="w-8 h-8 mb-2" data-id="element-1571" />
                  <p data-id="element-1572">Geen vragen zonder FamilyId!</p>
                </div>}
            </div> : filterType === 'taxonomy-gap' ? <div className="p-4 space-y-2" data-id="element-1573">
              <div className="text-sm font-bold text-rose-700 mb-3 flex items-center gap-2" data-id="element-1574">
                <XCircle className="w-4 h-4" data-id="element-1575" />
                {filteredGaps.length} families in taxonomy zonder seed-vragen
                <span className="text-xs font-normal text-slate-500 ml-1" data-id="element-1576">
                  (gesorteerd op tier)
                </span>
              </div>
              {filteredGaps.map(family => <div key={family.familyId} className="flex items-center justify-between p-3 bg-rose-50 border border-rose-100 rounded-lg" data-id="element-1577">
                  <div className="min-w-0 flex-1" data-id="element-1578">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-1579">
                      <span className="font-mono text-xs font-bold text-rose-800" data-id="element-1580">
                        {family.familyId}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded font-medium" data-id="element-1581">
                        Tier {family.tier} · {family.levelCode}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 italic" data-id="element-1582">
                      {family.description}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5" data-id="element-1583">
                      {family.skill} → {family.subSkill}
                    </div>
                  </div>
                  <Badge variant="danger" size="sm" data-id="element-1584">
                    Geen vragen
                  </Badge>
                </div>)}
              {filteredGaps.length === 0 && <div className="text-center py-12 text-emerald-600 flex flex-col items-center" data-id="element-1585">
                  <CheckCircle className="w-8 h-8 mb-2" data-id="element-1586" />
                  <p data-id="element-1587">Alle taxonomy families hebben seed-vragen!</p>
                </div>}
            </div> : <div className="divide-y divide-slate-100" data-id="element-1588">
              {filteredFamilies.length === 0 ? <div className="text-center py-12 text-slate-400" data-id="element-1589">
                  Geen families gevonden met deze filters
                </div> : filteredFamilies.map(group => {
            const isExpanded = expandedFamilies.has(group.id);
            return <div key={group.id} className="bg-white hover:bg-slate-50 transition-colors" data-id="element-1590">
                      <div className="flex items-center p-3 cursor-pointer" onClick={() => toggleFamily(group.id)} data-id="element-1591">
                        <div className="mr-3 text-slate-400" data-id="element-1592">
                          {isExpanded ? <ChevronDown className="w-4 h-4" data-id="element-1593" /> : <ChevronRight className="w-4 h-4" data-id="element-1594" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1595">
                          <div className="flex items-center gap-2 mb-1" data-id="element-1596">
                            <span className="font-mono font-bold text-sm text-slate-700" data-id="element-1597">
                              {group.id}
                            </span>
                            {group.isCrossLevel && <Badge variant="success" size="sm" icon={<Layers className="w-3 h-3" data-id="element-1599" />} data-id="element-1598">
                                Cross-Level
                              </Badge>}
                            {group.questions.length < 3 && <Badge variant="warning" size="sm" data-id="element-1600">
                                {group.questions.length} vraag
                                {group.questions.length !== 1 ? 'en' : ''} (min.
                                3)
                              </Badge>}
                          </div>
                          {group.definition && <div className="text-xs text-slate-600 mb-1 italic" data-id="element-1601">
                              {group.definition.description}
                            </div>}
                          <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-1602">
                            <span className="capitalize" data-id="element-1603">
                              {Array.from(group.subSkills)[0] || 'Unknown SubSkill'}
                            </span>
                            <span data-id="element-1604">•</span>
                            <span data-id="element-1605">{group.questions.length} vragen</span>
                            {group.definition && <>
                                <span data-id="element-1606">•</span>
                                <span className="font-medium text-indigo-600" data-id="element-1607">
                                  Tier {group.definition.tier} (
                                  {group.definition.levelCode})
                                </span>
                              </>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1608">
                          {Array.from(group.levels).sort((a, b) => a - b).map(level => {
                    const levelCode = tierToNiveauCode(level) || `T${level}`;
                    return <span key={level} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded" data-id="element-1609">
                                  {levelCode}
                                </span>;
                  })}
                        </div>
                      </div>
                      <AnimatePresence data-id="element-1610">
                        {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-1611">
                            <div className="p-3 pl-10 space-y-2" data-id="element-1612">
                              {group.questions.map(q => <div key={q.id} className="flex items-start gap-3 text-sm p-2 bg-white rounded border border-slate-200" data-id="element-1613">
                                  <div className="font-mono text-xs text-slate-400 mt-0.5" data-id="element-1614">
                                    {q.id}
                                  </div>
                                  <div className="flex-1" data-id="element-1615">
                                    <div className="text-slate-800" data-id="element-1616">
                                      {q.question}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-1 flex gap-2 items-center" data-id="element-1617">
                                      <span data-id="element-1618">Tier {q.tier || q.difficulty}</span>
                                      <span data-id="element-1619">•</span>
                                      <span data-id="element-1620">{q.questionType}</span>
                                      <span data-id="element-1621">•</span>
                                      <span className="font-mono" data-id="element-1622">
                                        {q.familyId}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
          })}
            </div>}
        </div>
      </div>
    </div>;
}
function StatCard({
  label,
  value,
  icon,
  subtext,
  isError = false,
  isWarning = false
}: StatCardProps) {
  let borderColor = 'border-slate-200';
  let bgColor = 'bg-white';
  let textColor = 'text-slate-900';
  if (isError) {
    borderColor = 'border-rose-200';
    bgColor = 'bg-rose-50';
    textColor = 'text-rose-900';
  } else if (isWarning) {
    borderColor = 'border-amber-200';
    bgColor = 'bg-amber-50';
    textColor = 'text-amber-900';
  }
  return <div className={`p-4 rounded-xl border ${borderColor} ${bgColor}`} data-id="element-1623">
      <div className="flex items-center justify-between mb-1" data-id="element-1624">
        <span className={`text-xs font-bold uppercase tracking-wider ${isError ? 'text-rose-600' : isWarning ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-1625">
          {label}
        </span>
        {icon}
      </div>
      <div className={`text-2xl font-bold ${textColor}`} data-id="element-1626">{value}</div>
      {subtext && <div className="text-[10px] text-slate-400 mt-1" data-id="element-1627">{subtext}</div>}
    </div>;
}
function FilterButton({
  active,
  onClick,
  label,
  icon
}: FilterButtonProps) {
  return <button onClick={onClick} className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
        ${active ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}
      `} data-id="element-1628">
      {icon}
      {label}
    </button>;
}